import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import moment from "moment";
import {
  Card, Badge, Button, Form, Spinner, Table
} from "react-bootstrap";
import {
  LuArrowLeft, LuUser, LuMail, LuPhone, LuMapPin,
  LuPencil, LuSave, LuX, LuPackage, LuFileText,
  LuTruck, LuTrash2, LuArrowUpRight, LuCircleDot,
  LuCalendar, LuDollarSign, LuMessageCircle, LuSend
} from "react-icons/lu";
import ComposeEmailModal from "../../../components/communications/ComposeEmailModal";
import WhatsAppInitiateModal from "../../../components/communications/WhatsAppInitiateModal";
import ConfirmModal from "../../../components/ConfirmModal";
import { getConversations } from "../../../redux/reducers/communicationSlice";
import { resendInvitation, sendInvitation } from "../../../redux/reducers/invitationSlice";
import PhoneInput from "../../../components/common/PhoneInput";
import { formatPhoneNumber } from "../../../utils/phoneUtils";

import {
  getCustomerDetail,
  updateCustomer,
  deleteCustomer,
  fetchCustomerInvoices,
  fetchCustomerShipments,
} from "../../../redux/reducers/customerSlice";
import { sendPortalInvite } from "../../../redux/reducers/orderSlice";
import { toast } from "react-toastify";

const AVATAR_COLORS = ["#28B0C4", "#6366f1", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6"];
const getAvatarColor = (id = "") =>
  AVATAR_COLORS[id.charCodeAt(id.length - 1) % AVATAR_COLORS.length];
const getInitials = (name = "") =>
  name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

function StatusBadge({ status }) {
  const map = {
    Paid: "success",
    Draft: "secondary",
    Sent: "info",
    Overdue: "danger",
    Cancelled: "danger",
    Pending: "warning",
    DELIVERED: "success",
    DISPATCHED: "info",
    PENDING: "secondary",
    OUT_FOR_DELIVERY: "primary",
    FAILED: "danger",
    RETURNED: "warning",
    CANCELLED: "danger",
    PROCESSING: "warning",
  };
  const variant = map[status] || "secondary";
  return (
    <Badge bg={`${variant}-subtle`} className={`text-${variant} rounded-pill px-3 fw-semibold`}>
      {status}
    </Badge>
  );
}

export default function ViewCustomer() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const selectedBusiness = useSelector((state) => state.business.selectedBusiness);
  const businessId = selectedBusiness?._id;
  const { customerDetail, customerInvoices, customerShipments, loading } = useSelector(
    (state) => state.customer
  );
  console.log(customerDetail);
  const [activeTab, setActiveTab] = useState("invoices");
  const [editing, setEditing] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: { countryCode: '+1', number: "" }, currency: "",
    address: { street: "", city: "", state: "", country: "", zipCode: "" },
  });

  const [showComposeModal, setShowComposeModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);

  const { conversations } = useSelector((state) => state.communication);
  const customerConversations = conversations.filter(c => c.customerId?._id === id || c.customerId === id);

  // Load data on mount
  useEffect(() => {
    if (id && businessId) {
      dispatch(getCustomerDetail({ customerId: id, businessId }));
      dispatch(fetchCustomerInvoices({ customerId: id, businessId }));
      dispatch(fetchCustomerShipments({ customerId: id, businessId }));
      dispatch(getConversations({ customerId: id }));
    }
  }, [dispatch, id, businessId]);

  // Populate form when customer loads
  useEffect(() => {
    if (customerDetail) {
      setForm({
        id: customerDetail._id,
        name: customerDetail.name || "",
        email: customerDetail.email || "",
        phone: typeof customerDetail.phone === 'string' 
          ? { countryCode: '+1', number: customerDetail.phone } 
          : customerDetail.phone || { countryCode: '+1', number: "" },
        currency: customerDetail.currency || "USD",
        business: businessId,
        address: {
          street: customerDetail.address?.street || "",
          city: customerDetail.address?.city || "",
          state: customerDetail.address?.state || "",
          country: customerDetail.address?.country || "",
          zipCode: customerDetail.address?.zipCode || "",
        },
      });
    }
  }, [customerDetail, businessId]);

  const handleSave = () => {
    dispatch(updateCustomer(form)).then((res) => {
      if (!res.error) {
        setEditing(false);
        toast.success("Customer updated successfully");
        dispatch(getCustomerDetail({ customerId: id, businessId }));
      }
    });
  };

  const handleDelete = () => {
    dispatch(deleteCustomer(id)).then((res) => {
      if (!res.error) navigate("/customer");
    });
  };

  const [inviting, setInviting] = useState(false);
  const handleInvite = () => {
    setInviting(true);
    if (c.portalAuth?.isInvited) {
      dispatch(resendInvitation(id)).finally(() => setInviting(false));
    } else {
      dispatch(sendInvitation({ email: c.email, name: c.name, businessId })).finally(() => setInviting(false));
    }
  };

  const handleFieldChange = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setForm((f) => ({ ...f, [parent]: { ...f[parent], [child]: value } }));
    } else {
      setForm((f) => ({ ...f, [field]: value }));
    }
  };

  if (loading && !customerDetail) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!customerDetail) {
    return (
      <div className="text-center py-5 text-muted">
        <LuCircleDot size={40} className="mb-3 opacity-25" />
        <p>Customer not found.</p>
        <Button variant="light" className="rounded-pill" onClick={() => navigate("/customer")}>
          Back to Customers
        </Button>
      </div>
    );
  }

  const c = customerDetail;
  const color = getAvatarColor(c._id);

  return (
    <div className="container-fluid my-4">

      {/* ── Header ─────────────────────────────────────── */}
      <div className="d-flex align-items-center gap-3 mb-4">
        <button className="btn btn-light rounded-circle p-2" onClick={() => navigate("/customer")}>
          <LuArrowLeft size={16} />
        </button>
        <div className="d-flex align-items-center gap-3">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white flex-shrink-0"
            style={{ width: 48, height: 48, fontSize: 16, backgroundColor: color }}
          >
            {getInitials(c.name)}
          </div>
          <div>
            <h5 className="fw-bold text-dark mb-0">{c.name}</h5>
            <div className="text-muted" style={{ fontSize: 12 }}>
              Customer since {moment(c.createdAt).format("MMM D, YYYY")}
            </div>
          </div>
        </div>
        <div className="ms-auto d-flex gap-2">
          {!editing ? (
            <Button
              variant="outline-primary"
              className="rounded-pill px-3 d-flex align-items-center gap-2 fw-semibold"
              size="sm"
              onClick={() => setEditing(true)}
            >
              <LuPencil size={14} /> Edit
            </Button>
          ) : (
            <>
              <Button
                variant="light"
                className="rounded-pill px-3 d-flex align-items-center gap-2 fw-semibold"
                size="sm"
                onClick={() => setEditing(false)}
                disabled={loading}
              >
                <LuX size={14} /> Cancel
              </Button>
              <Button
                variant="primary"
                className="rounded-pill px-3 d-flex align-items-center gap-2 fw-semibold"
                size="sm"
                onClick={handleSave}
                disabled={loading}
              >
                <LuSave size={14} /> {loading ? "Saving..." : "Save"}
              </Button>
            </>
          )}
          <Button
            variant="outline-danger"
            className="rounded-pill px-3 d-flex align-items-center gap-2 fw-semibold"
            size="sm"
            onClick={() => setDeleteModal(true)}
          >
            <LuTrash2 size={14} /> Delete
          </Button>
        </div>
      </div>

      <div className="row g-4">

        {/* ── LEFT COLUMN ─────────────────────────────── */}
        <div className="col-12 col-lg-8">

          {/* Profile Card */}
          <Card className="border-0 shadow-sm rounded-4 mb-4" style={{ padding: "24px" }}>
            <div className="d-flex align-items-center gap-2 mb-4">
              <div className="rounded-2" style={{ width: 4, height: 18, background: "#28B0C4" }} />
              <h6 className="fw-bold text-dark mb-0" style={{ fontSize: 14 }}>
                Customer Profile
              </h6>
            </div>

            <div className="row g-3">
              {/* Name */}
              <div className="col-md-6">
                <label className="small fw-semibold text-muted text-uppercase mb-1">
                  <LuUser size={12} className="me-1" />Full Name
                </label>
                {editing ? (
                  <Form.Control
                    className="bg-light border-0 rounded-3 fw-semibold"
                    value={form.name}
                    onChange={(e) => handleFieldChange("name", e.target.value)}
                  />
                ) : (
                  <div className="fw-semibold text-dark">{c.name || "—"}</div>
                )}
              </div>

              {/* Email */}
              <div className="col-md-6">
                <label className="small fw-semibold text-muted text-uppercase mb-1">
                  <LuMail size={12} className="me-1" />Email
                </label>
                {editing ? (
                  <Form.Control
                    type="email"
                    className="bg-light border-0 rounded-3"
                    value={form.email}
                    onChange={(e) => handleFieldChange("email", e.target.value)}
                  />
                ) : (
                  <div className="small text-dark">{c.email || "—"}</div>
                )}
              </div>

              {/* Mobile */}
              <div className="col-md-6">
                <label className="small fw-semibold text-muted text-uppercase mb-1">
                  <LuPhone size={12} className="me-1" />Phone Number
                </label>
                {editing ? (
                  <PhoneInput
                    value={form.phone}
                    onChange={(val) => setForm({ ...form, phone: val })}
                  />
                ) : (
                  <div className="small text-dark">{formatPhoneNumber(c.phone)}</div>
                )}
              </div>

              {/* Currency */}
              <div className="col-md-6">
                <label className="small fw-semibold text-muted text-uppercase mb-1">
                  <LuDollarSign size={12} className="me-1" />Currency
                </label>
                {editing ? (
                  <Form.Control
                    className="bg-light border-0 rounded-3"
                    value={form.currency}
                    onChange={(e) => handleFieldChange("currency", e.target.value)}
                    placeholder="USD"
                  />
                ) : (
                  <div className="small text-dark fw-semibold">{c.currency || "USD"}</div>
                )}
              </div>

              {/* Address section */}
              <div className="col-12 mt-2">
                <div className="border-top pt-3">
                  <label className="small fw-bold text-muted text-uppercase mb-3 d-flex align-items-center gap-1">
                    <LuMapPin size={12} />Address
                  </label>
                  <div className="row g-3">
                    {[
                      { field: "address.street", label: "Street", placeholder: "123 Main St" },
                      { field: "address.city", label: "City", placeholder: "New York" },
                      { field: "address.state", label: "State", placeholder: "NY" },
                      { field: "address.country", label: "Country", placeholder: "Canada" },
                      { field: "address.zipCode", label: "Zip Code", placeholder: "10001" },
                    ].map(({ field, label, placeholder }) => {
                      const keys = field.split(".");
                      const val = keys.length === 2 ? (c[keys[0]] || {})[keys[1]] : c[keys[0]];
                      const formVal = keys.length === 2 ? (form[keys[0]] || {})[keys[1]] : form[keys[0]];
                      return (
                        <div className="col-md-6" key={field}>
                          <label className="small text-muted mb-1">{label}</label>
                          {editing ? (
                            <Form.Control
                              className="bg-light border-0 rounded-3"
                              value={formVal || ""}
                              onChange={(e) => handleFieldChange(field, e.target.value)}
                              placeholder={placeholder}
                            />
                          ) : (
                            <div className="small text-dark">{val || "—"}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* ── TABS ── */}
          <Card className="border-0 shadow-sm rounded-4" style={{ padding: "24px" }}>
            <div className="d-flex gap-3 mb-4 border-bottom pb-3">
              {[
                { key: "invoices", label: "Invoices", icon: <LuFileText size={14} /> },
                { key: "shipments", label: "Shipments", icon: <LuTruck size={14} /> },
              ].map(({ key, label, icon }) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`btn btn-sm d-flex align-items-center gap-2 fw-semibold px-3 rounded-pill ${
                    activeTab === key
                      ? "btn-primary"
                      : "btn-light text-muted"
                  }`}
                >
                  {icon} {label}
                  <span className="badge bg-light text-dark ms-1 rounded-pill" style={{ fontSize: 10 }}>
                    {key === "invoices" ? customerInvoices.length : customerShipments.length}
                  </span>
                </button>
              ))}
            </div>

            {/* Invoices Tab */}
            {activeTab === "invoices" && (
              customerInvoices.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <LuFileText size={36} className="mb-2 opacity-25" />
                  <p className="mb-0 small">No invoices found for this customer.</p>
                </div>
              ) : (
                <Table responsive hover className="align-middle mb-0">
                  <thead className="bg-light text-muted small text-uppercase">
                    <tr>
                      <th className="ps-3 py-3 fw-semibold">Invoice</th>
                      <th className="fw-semibold">Date</th>
                      <th className="fw-semibold">Amount</th>
                      <th className="fw-semibold">Status</th>
                      <th className="pe-3 text-end fw-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerInvoices.map((inv) => (
                      <tr key={inv._id}>
                        <td className="ps-3 py-3">
                          <div className="fw-bold small">
                            #{inv._id?.slice(-6).toUpperCase()}
                          </div>
                          <div className="text-muted" style={{ fontSize: 11 }}>
                            {inv.description || "—"}
                          </div>
                        </td>
                        <td>
                          <div className="small">
                            {inv.invoiceDate ? moment(inv.invoiceDate).format("MMM D, YYYY") : "—"}
                          </div>
                          {inv.invoiceDueDate && (
                            <div className="text-muted" style={{ fontSize: 11 }}>
                              Due {moment(inv.invoiceDueDate).format("MMM D")}
                            </div>
                          )}
                        </td>
                        <td>
                          <span className="fw-bold text-brand">
                            {inv.totalAmount != null
                              ? `${inv.currency || ""} ${Number(inv.totalAmount).toFixed(2)}`
                              : "—"}
                          </span>
                        </td>
                        <td>
                          <StatusBadge status={inv.status} />
                        </td>
                        <td className="pe-3 text-end">
                          <Button
                            variant="light"
                            size="sm"
                            className="rounded-circle p-2"
                            onClick={() => navigate(`/view-invoice/${inv._id}`)}
                          >
                            <LuArrowUpRight size={16} className="text-muted" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )
            )}

            {/* Shipments Tab */}
            {activeTab === "shipments" && (
              customerShipments.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <LuPackage size={36} className="mb-2 opacity-25" />
                  <p className="mb-0 small">No shipments found for this customer.</p>
                </div>
              ) : (
                <Table responsive hover className="align-middle mb-0">
                  <thead className="bg-light text-muted small text-uppercase">
                    <tr>
                      <th className="ps-3 py-3 fw-semibold">Tracking</th>
                      <th className="fw-semibold">Type</th>
                      <th className="fw-semibold">ETA</th>
                      <th className="fw-semibold">Status</th>
                      <th className="pe-3 text-end fw-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerShipments.map((shp) => (
                      <tr key={shp._id}>
                        <td className="ps-3 py-3">
                          <div className="fw-bold small font-monospace">{shp.trackingCode}</div>
                          <div className="text-muted" style={{ fontSize: 11 }}>
                            {moment(shp.createdAt).format("MMM D, YYYY")}
                          </div>
                        </td>
                        <td>
                          <span className="small text-muted fw-semibold">
                            {shp.deliveryType === "self" ? "Self Delivery" : "3rd Party"}
                          </span>
                        </td>
                        <td>
                          <div className="small">
                            {shp.estimatedArrival?.current
                              ? moment(shp.estimatedArrival.current).format("MMM D, YYYY")
                              : "—"}
                          </div>
                          {shp.estimatedArrival?.isDelayed && (
                            <span className="badge text-danger bg-danger-subtle rounded-pill" style={{ fontSize: 10 }}>
                              Delayed
                            </span>
                          )}
                        </td>
                        <td>
                          <StatusBadge status={shp.status} />
                        </td>
                        <td className="pe-3 text-end">
                          <Button
                            variant="light"
                            size="sm"
                            className="rounded-circle p-2"
                            onClick={() => navigate(`/shipment/${shp._id}`)}
                          >
                            <LuArrowUpRight size={16} className="text-muted" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )
            )}
          </Card>
        </div>

        {/* ── RIGHT COLUMN ─────────────────────────────── */}
        <div className="col-12 col-lg-4">

          {/* Portal Access */}
          <Card className="border-0 shadow-sm rounded-4 mb-4 overflow-hidden" style={{ padding: "20px" }}>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-center gap-2">
                <div className="rounded-2" style={{ width: 4, height: 18, background: "#28B0C4" }} />
                <h6 className="fw-bold text-dark mb-0" style={{ fontSize: 14 }}>Portal Access</h6>
              </div>
              {c.portalAuth?.isActivated ? (
                <Badge bg="success-subtle" className="text-success rounded-pill px-2 fw-bold">Active</Badge>
              ) : c.portalAuth?.isInvited ? (
                <Badge bg="info-subtle" className="text-info rounded-pill px-2 fw-bold">Invited</Badge>
              ) : (
                <Badge bg="secondary-subtle" className="text-muted rounded-pill px-2 fw-bold">No Access</Badge>
              )}
            </div>

            <div className="bg-light rounded-4 p-3 mb-3">
              {c.portalAuth?.isActivated ? (
                <div className="small text-muted">
                  Last login: <span className="text-dark fw-semibold">{c.portalAuth.lastLogin ? moment(c.portalAuth.lastLogin).fromNow() : 'Never'}</span>
                </div>
              ) : c.portalAuth?.isInvited ? (
                <div className="small text-muted">
                  Invite sent: <span className="text-dark fw-semibold">{moment(c.portalAuth.inviteTokenExpiry).subtract(48, 'hours').format('MMM D, YYYY')}</span>
                </div>
              ) : (
                <p className="extra-small text-muted mb-0">This customer hasn't been invited to the portal yet.</p>
              )}
            </div>

            <Button 
              className="w-100 rounded-3 py-2 fw-bold text-white border-0 small shadow-sm"
              style={{ background: "#28B0C4" }}
              onClick={handleInvite}
              disabled={inviting || c.portalAuth?.isActivated}
            >
              {inviting ? <Spinner animation="border" size="sm" /> : c.portalAuth?.isActivated ? 'Access Active' : c.portalAuth?.isInvited ? 'Resend Portal Invite' : 'Send Portal Invitation'}
            </Button>
          </Card>

          {/* Communication Card */}
          <Card className="border-0 shadow-sm rounded-4 mb-4" style={{ padding: "20px" }}>
            <div className="d-flex align-items-center gap-2 mb-3">
              <div className="rounded-2" style={{ width: 4, height: 18, background: "#28B0C4" }} />
              <h6 className="fw-bold text-dark mb-0" style={{ fontSize: "14px" }}>Communication</h6>
            </div>

            <div className="d-flex flex-column gap-2">
              <Button className="btn-light rounded-3 fw-semibold d-flex align-items-center gap-2 w-100 border"
                style={{ fontSize: '13px', padding: '10px' }}
                onClick={() => setShowComposeModal(true)}>
                <LuMail size={14} /> Send Email
              </Button>
              <Button className="btn-light rounded-3 fw-semibold d-flex align-items-center gap-2 w-100 border"
                style={{ fontSize: '13px', padding: '10px' }}
                onClick={() => setShowWhatsAppModal(true)}>
                <LuMessageCircle size={14} style={{ color: '#10b981' }} /> Start WhatsApp
              </Button>
            </div>

            {customerConversations.length > 0 && (
              <div className="mt-3 pt-3 border-top">
                <div className="text-muted fw-semibold mb-2"
                  style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                  Recent Conversations
                </div>
                {customerConversations.slice(0, 3).map(conv => (
                  <div key={conv._id}
                    className="d-flex align-items-center gap-2 py-2 border-bottom"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/communications`)}>
                    {conv.channel === 'email'
                      ? <LuMail size={12} className="text-muted flex-shrink-0" />
                      : <LuMessageCircle size={12} style={{ color: '#10b981', flexShrink: 0 }} />}
                    <span className="small text-truncate flex-grow-1">
                      {conv.subject || 'WhatsApp chat'}
                    </span>
                    <span className="text-muted flex-shrink-0" style={{ fontSize: '10px' }}>
                      {moment(conv.lastMessageAt).fromNow()}
                    </span>
                  </div>
                ))}
                <Button variant="link" className="p-0 mt-2 d-block"
                  style={{ fontSize: '12px', color: '#28B0C4', textDecoration: 'none' }}
                  onClick={() => navigate(`/communications?customerId=${id}`)}>
                  View all conversations →
                </Button>
              </div>
            )}
          </Card>

          {/* Stats */}
          <Card className="border-0 shadow-sm rounded-4 mb-4" style={{ padding: "20px" }}>
            <div className="d-flex align-items-center gap-2 mb-3">
              <div className="rounded-2" style={{ width: 4, height: 18, background: "#6366f1" }} />
              <h6 className="fw-bold text-dark mb-0" style={{ fontSize: 14 }}>Overview</h6>
            </div>
            <div className="d-flex flex-column gap-3">
              {[
                {
                  icon: <LuFileText size={18} color="#28B0C4" />,
                  label: "Total Invoices",
                  value: customerInvoices.length,
                  bg: "rgba(40,176,196,0.1)"
                },
                {
                  icon: <LuTruck size={18} color="#6366f1" />,
                  label: "Total Shipments",
                  value: customerShipments.length,
                  bg: "rgba(99,102,241,0.1)"
                },
                {
                  icon: <LuCalendar size={18} color="#f59e0b" />,
                  label: "Customer Since",
                  value: moment(c.createdAt).format("MMM D, YYYY"),
                  bg: "rgba(245,158,11,0.1)"
                },
              ].map(({ icon, label, value, bg }) => (
                <div
                  key={label}
                  className="d-flex align-items-center gap-3 p-3 rounded-4"
                  style={{ background: bg }}
                >
                  <div>{icon}</div>
                  <div>
                    <div className="text-muted small">{label}</div>
                    <div className="fw-bold text-dark">{value}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Contact info */}
          <Card className="border-0 shadow-sm rounded-4 mb-4" style={{ padding: "20px" }}>
            <div className="d-flex align-items-center gap-2 mb-3">
              <div className="rounded-2" style={{ width: 4, height: 18, background: "#10b981" }} />
              <h6 className="fw-bold text-dark mb-0" style={{ fontSize: 14 }}>Contact</h6>
            </div>
            <div className="d-flex flex-column gap-2">
              <div className="d-flex align-items-center gap-2">
                <LuMail size={14} className="text-muted flex-shrink-0" />
                <span className="small text-dark text-break">{c.email || "—"}</span>
              </div>
               <div className="d-flex align-items-center gap-2">
                <LuPhone size={14} className="text-muted flex-shrink-0" />
                <span className="small text-dark">{formatPhoneNumber(c.phone)}</span>
              </div>
              {c.address?.country && (
                <div className="d-flex align-items-start gap-2 mt-1">
                  <LuMapPin size={14} className="text-muted flex-shrink-0 mt-1" />
                  <span className="small text-muted">
                    {[c.address?.city, c.address?.state, c.address?.country]
                      .filter(Boolean)
                      .join(", ")}
                  </span>
                </div>
              )}
            </div>
          </Card>

          {/* Invoice value summary */}
          {customerInvoices.length > 0 && (
            <Card className="border-0 shadow-sm rounded-4" style={{ padding: "20px" }}>
              <div className="d-flex align-items-center gap-2 mb-3">
                <div className="rounded-2" style={{ width: 4, height: 18, background: "#f59e0b" }} />
                <h6 className="fw-bold text-dark mb-0" style={{ fontSize: 14 }}>Invoice Summary</h6>
              </div>
              <div className="d-flex flex-column gap-2">
                {[
                  {
                    label: "Total Billed",
                    value: customerInvoices.reduce((s, i) => s + (i.totalAmount || 0), 0),
                    color: "#28B0C4"
                  },
                  {
                    label: "Paid",
                    value: customerInvoices
                      .filter((i) => i.status === "Paid")
                      .reduce((s, i) => s + (i.totalAmount || 0), 0),
                    color: "#10b981"
                  },
                  {
                    label: "Outstanding",
                    value: customerInvoices
                      .filter((i) => i.status !== "Paid" && i.status !== "Cancelled")
                      .reduce((s, i) => s + (i.totalAmount || 0), 0),
                    color: "#ef4444"
                  },
                ].map(({ label, value, color }) => (
                  <div key={label} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                    <span className="small text-muted">{label}</span>
                    <span className="fw-bold small" style={{ color }}>
                      {customerInvoices[0]?.currency || ""} {Number(value).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>

      <ConfirmModal
        show={deleteModal}
        onHide={() => !loading && setDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Customer"
        message={`Are you sure you want to delete ${c.name}? This action is permanent and cannot be undone.`}
        confirmText="Delete Customer"
        variant="danger"
        icon={LuTrash2}
        loading={loading}
      />

      <ComposeEmailModal show={showComposeModal} onHide={() => setShowComposeModal(false)} />
      <WhatsAppInitiateModal show={showWhatsAppModal} onHide={() => setShowWhatsAppModal(false)} />
    </div>

  );
}
